import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import VarietyField from "@/components/variety-field";
import { DIFFICULTY, type Difficulty as DifficultyEnum } from "@/integrations/backend/enums";
import type { CreateProgramInput, Program } from "@/integrations/backend/programs";
import { labels, motivationOptions, programExerciseTypeOptions } from "@/lib/onboarding-options";

export type ProgramEditorValue = CreateProgramInput;

export function createDefaultProgramEditorValue(): ProgramEditorValue {
  return {
    name: "",
    description: "",
    duration: 12,
    frequency: 3,
    weekly_schedule_preference: "",
    difficulty: "Beginner",
    motivations: [],
    exercise_types: [],
    exercise_type_details: {},
    program_focus: "",
    what_to_expect: "",
    variety: "",
  };
}

export function programToEditorValue(program: Program): ProgramEditorValue {
  return {
    name: program.name,
    description: program.description,
    duration: program.duration,
    frequency: program.frequency,
    weekly_schedule_preference: program.weekly_schedule_preference ?? "",
    difficulty: program.difficulty,
    motivations: [...program.motivations],
    exercise_types: [...program.exercise_types],
    exercise_type_details: { ...(program.exercise_type_details ?? {}) },
    program_focus: program.program_focus ?? "",
    what_to_expect: program.what_to_expect ?? "",
    variety: program.variety ?? "",
  };
}

interface ProgramEditorFieldsProps {
  value: ProgramEditorValue;
  onChange: (value: ProgramEditorValue) => void;
}

export default function ProgramEditorFields({ value, onChange }: ProgramEditorFieldsProps) {
  const updateField = <K extends keyof ProgramEditorValue>(key: K, nextValue: ProgramEditorValue[K]) => {
    onChange({ ...value, [key]: nextValue });
  };

  const toggleMotivation = (motivationId: string) => {
    onChange({
      ...value,
      motivations: value.motivations.includes(motivationId)
        ? value.motivations.filter((id) => id !== motivationId)
        : [...value.motivations, motivationId],
    });
  };

  const toggleExerciseType = (exerciseTypeId: string) => {
    const nextExerciseTypes = value.exercise_types.includes(exerciseTypeId)
      ? value.exercise_types.filter((id) => id !== exerciseTypeId)
      : [...value.exercise_types, exerciseTypeId];

    const nextDetails = Object.fromEntries(
      Object.entries(value.exercise_type_details ?? {}).filter(([key]) => nextExerciseTypes.includes(key))
    );

    onChange({
      ...value,
      exercise_types: nextExerciseTypes,
      exercise_type_details: nextDetails,
    });
  };

  return (
    <div className="space-y-4 py-2">
      <div className="space-y-2">
        <Label htmlFor="program-editor-name">Name *</Label>
        <Input
          id="program-editor-name"
          value={value.name}
          onChange={(e) => updateField("name", e.target.value)}
          placeholder="e.g., 12 Week Strength Foundation"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="program-editor-description">Description *</Label>
        <Textarea
          id="program-editor-description"
          value={value.description}
          onChange={(e) => updateField("description", e.target.value)}
          placeholder="Describe the program goals and approach"
          rows={3}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="program-editor-duration">Duration (weeks) *</Label>
          <Input
            id="program-editor-duration"
            type="number"
            min="1"
            value={value.duration}
            onChange={(e) => updateField("duration", parseInt(e.target.value, 10) || 0)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="program-editor-frequency">Frequency (sessions/week) *</Label>
          <Input
            id="program-editor-frequency"
            type="number"
            min="1"
            value={value.frequency}
            onChange={(e) => updateField("frequency", parseInt(e.target.value, 10) || 0)}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="program-editor-weekly-schedule-preference">
          Preferred Workout Scheduling Across the Week
        </Label>
        <Textarea
          id="program-editor-weekly-schedule-preference"
          value={value.weekly_schedule_preference ?? ""}
          onChange={(e) => updateField("weekly_schedule_preference", e.target.value)}
          placeholder="Optional: e.g., lift Mon/Wed/Fri, conditioning Tue/Sat"
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="program-editor-difficulty">Difficulty</Label>
        <Select value={value.difficulty} onValueChange={(next) => updateField("difficulty", next as DifficultyEnum)}>
          <SelectTrigger id="program-editor-difficulty">
            <SelectValue placeholder="Select difficulty level" />
          </SelectTrigger>
          <SelectContent>
            {(DIFFICULTY as unknown as string[]).map((difficulty) => (
              <SelectItem key={difficulty} value={difficulty}>
                {difficulty}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Motivations</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 border rounded-lg">
          {motivationOptions.map((motivation) => (
            <div key={motivation.id} className="flex items-center space-x-2">
              <Checkbox
                id={`program-editor-motivation-${motivation.id}`}
                checked={value.motivations.includes(motivation.id)}
                onCheckedChange={() => toggleMotivation(motivation.id)}
              />
              <Label
                htmlFor={`program-editor-motivation-${motivation.id}`}
                className="text-sm font-normal cursor-pointer"
              >
                {motivation.icon} {motivation.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Exercise Types</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 border rounded-lg">
          {programExerciseTypeOptions.map((exerciseType) => (
            <div key={exerciseType.id} className="flex items-center space-x-2">
              <Checkbox
                id={`program-editor-exercise-type-${exerciseType.id}`}
                checked={value.exercise_types.includes(exerciseType.id)}
                onCheckedChange={() => toggleExerciseType(exerciseType.id)}
              />
              <Label
                htmlFor={`program-editor-exercise-type-${exerciseType.id}`}
                className="text-sm font-normal cursor-pointer"
              >
                {exerciseType.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {value.exercise_types.length > 0 && (
        <div className="space-y-3">
          <Label>Exercise Type Details</Label>
          <div className="space-y-3">
            {value.exercise_types.map((exerciseTypeId) => {
              const label = labels.exerciseType[exerciseTypeId] ?? exerciseTypeId;
              return (
                <div key={exerciseTypeId} className="space-y-2">
                  <Label htmlFor={`program-editor-exercise-detail-${exerciseTypeId}`} className="text-sm font-medium">
                    {label}
                  </Label>
                  <Textarea
                    id={`program-editor-exercise-detail-${exerciseTypeId}`}
                    value={value.exercise_type_details?.[exerciseTypeId] ?? ""}
                    onChange={(e) =>
                      updateField("exercise_type_details", {
                        ...(value.exercise_type_details ?? {}),
                        [exerciseTypeId]: e.target.value,
                      })
                    }
                    placeholder={`Add details for ${label} (optional)`}
                    rows={2}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="program-editor-focus">Program Focus</Label>
        <Textarea
          id="program-editor-focus"
          value={value.program_focus ?? ""}
          onChange={(e) => updateField("program_focus", e.target.value)}
          placeholder="What is the main focus of this program?"
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="program-editor-what-to-expect">What to Expect</Label>
        <Textarea
          id="program-editor-what-to-expect"
          value={value.what_to_expect ?? ""}
          onChange={(e) => updateField("what_to_expect", e.target.value)}
          placeholder="What can the client expect from this program?"
          rows={2}
        />
      </div>

      <VarietyField
        id="program-editor-variety"
        value={value.variety ?? ""}
        onChange={(next) => updateField("variety", next)}
      />
    </div>
  );
}
